import { useMemo } from 'react';
import { useAppIdeaFavorites } from '@/hooks/app-library/use-app-idea-favorites';

/**
 * Hook for fetching top app requests for Community sidebar
 * Returns the top 5 most liked app requests optimized for compact display
 */
export const useCommunityAppRequests = () => {
  const { 
    appIdeas, 
    isLoading, 
    hasError, 
    isAppIdeaFavorited,
    handleToggleFavorite,
    getAppIdeaFavoriteCount
  } = useAppIdeaFavorites();

  // Get top 5 app requests sorted by favorite count
  const topAppRequests = useMemo(() => {
    if (!appIdeas || appIdeas.length === 0) return [];
    
    return appIdeas
      .map(idea => ({
        // ✅ CRITICAL FIX: Preserve ALL original app idea data including research fields
        ...idea,
        // Override only the display fields for compact cards
        name: idea.name || idea.generated_title || 'Untitled App',
        description: idea.description || 'No description available',
        category: idea.category || 'general',
        icon_name: idea.icon_name || 'Package',
        // Add computed fields for compact display
        isFavorited: isAppIdeaFavorited(idea.id),
        progressPercentage: Math.min(((idea.favorite_count || 0) / 100) * 100, 100),
        isReadyForDevelopment: (idea.favorite_count || 0) >= 100
      }))
      .sort((a, b) => b.favorite_count - a.favorite_count) // Sort by favorite count descending
      .slice(0, 5); // Take top 5
  }, [appIdeas, isAppIdeaFavorited]);

  // Enhanced toggle function that works with compact card format
  const handleToggleAppRequestFavorite = useMemo(() => {
    return async (appId: string) => {
      try {
        await handleToggleFavorite(appId);
      } catch (error) {
        console.error('Error toggling app request favorite:', error);
      }
    };
  }, [handleToggleFavorite]);

  return {
    topAppRequests,
    isLoading,
    hasError,
    handleToggleAppRequestFavorite,
    totalAppRequests: appIdeas?.length || 0,
    isEmpty: topAppRequests.length === 0
  };
}; 